import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link, useLocation } from "react-router-dom";
import {
  Box,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  IconButton,
  Typography,
  Tooltip,
  useTheme,
} from "@mui/material";

import BusinessIcon from "@mui/icons-material/Business";
import DescriptionIcon from "@mui/icons-material/Description";
import GavelIcon from "@mui/icons-material/Gavel";
import GroupsIcon from "@mui/icons-material/Groups";
import EventNoteIcon from "@mui/icons-material/EventNote";
import AssessmentIcon from "@mui/icons-material/Assessment";
import FileCopyIcon from "@mui/icons-material/FileCopy";

import TaskIcon from "@mui/icons-material/Task";
import MenuIcon from "@mui/icons-material/Menu";
import { KeyboardArrowDown, KeyboardArrowRight } from "@mui/icons-material";

const CsSidebar = ({ onSidebarToggle, navbarHeight = 64 }) => {
  const theme = useTheme();
  const location = useLocation();
  const [activeMenu, setActiveMenu] = useState(null);
  const [collapsed, setCollapsed] = useState(false);

  const handleMenuClick = (index) => {
    if (!collapsed) {
      setActiveMenu(activeMenu === index ? null : index);
    }
  };

  const handleDrawerToggle = () => {
    const newState = !collapsed;
    setCollapsed(newState);

    if (onSidebarToggle) {
      onSidebarToggle(!newState);
    }
  };

  useEffect(() => {
    if (location.pathname.includes("/cs/Client")) {
      setActiveMenu(1);
    }
  }, [location.pathname]);

  const menuItems = [
    {
      id: 0,
      title: "Company Overview",
      icon: <BusinessIcon />,
      path: "/csdashboard",
      subItems: null,
    },
    {
      id: 1,
      title: "Clients",
      icon: <BusinessIcon />,
      path: "/cs/Clients",
      subItems: [
        {
          id: 0,
          name: "Client Selection",
          path: "/cs/ClientSelection",
          icon: <BusinessIcon />,
        },
        {
          id: 1,
          name: "Client Companies",
          path: "/cs/ClientCompanies",
          icon: <BusinessIcon />,
        },

        {
          id: 3,
          name: "Client Tasks",
          path: "/cs/ClientTasks",
          icon: <TaskIcon />,
        },
      ],
    },
    {
      id: 2,
      title: "Sub Users",
      icon: <BusinessIcon />,
      path: "/cs/Users",
      subItems: [
        {
          id: 0,
          name: "User Creation",
          path: "/cs/SubUserCreation",
          icon: <BusinessIcon />,
        },
        {
          id: 1,
          name: "Sub User Management",
          path: "/cs/SubUserManagement",
          icon: <BusinessIcon />,
        },
      ],
    },
    {
      id: 3,
      title: "Compliance",
      icon: <GavelIcon />,
      path: "/compliance",
      subItems: null,
    },
    {
      id: 4,
      title: "Board Meetings",
      icon: <GroupsIcon />,
      path: "/CsboardMeeting",
      subItems: null,
    },
    {
      id: 5,
      title: "Annual Filings",
      icon: <FileCopyIcon />,
      path: "/upload",
      subItems: null,
    },
    {
      id: 6,
      title: "Calendar",
      icon: <EventNoteIcon />,
      path: "/CsCalenderPage",
      subItems: null,
    },
    {
      id: 7,
      title: "Documents",
      icon: <DescriptionIcon />,
      path: "/userDocument",
      subItems: null,
    },
    {
      id: 8,
      title: "Reports",
      icon: <AssessmentIcon />,
      path: "/CsReport",
      subItems: null,
    },
  ];

  const drawerWidth = collapsed ? 72 : 280;

  const renderMenuItem = (item) => (
    <Box key={item.id}>
      {item.subItems ? (
        <>
          <ListItemButton
            onClick={() => handleMenuClick(item.id)}
            selected={activeMenu === item.id}
            sx={{
              minHeight: 48,
              px: collapsed ? 1.5 : 2.5,
              margin: "4px 16px",
              borderRadius: "8px",
              transition: "all 0.2s ease-in-out",
              "&:hover": {
                backgroundColor: theme.palette.action.hover,
                transform: "translateX(4px)",
                "& .MuiListItemText-primary": {
                  color: theme.palette.primary.main,
                  fontWeight: "bold",
                },
                "& .MuiListItemIcon-root": {
                  color: theme.palette.primary.main,
                },
              },
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: 0,
                mr: collapsed ? 0 : 3,
                justifyContent: "center",
                color: theme.palette.primary.main,
              }}
            >
              <Tooltip
                title={item.title}
                placement="right"
                disableHoverListener={!collapsed}
              >
                {item.icon}
              </Tooltip>
            </ListItemIcon>
            {!collapsed && (
              <>
                <ListItemText
                  primary={item.title}
                  sx={{
                    "& .MuiListItemText-primary": {
                      fontSize: "0.875rem",
                      fontWeight: 500,
                    },
                  }}
                />
                {activeMenu === item.id ? (
                  <KeyboardArrowRight />
                ) : (
                  <KeyboardArrowDown />
                )}
              </>
            )}
          </ListItemButton>

          {!collapsed && (
            <Collapse in={activeMenu === item.id} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {item.subItems.map((subItem) => (
                  <ListItemButton
                    key={subItem.id}
                    component={Link}
                    to={subItem.path}
                    selected={location.pathname === subItem.path}
                    sx={{
                      pl: 4,
                      margin: "2px 16px",
                      borderRadius: "8px",
                      transition: "all 0.2s ease-in-out",
                      "&:hover": {
                        backgroundColor: theme.palette.action.hover,
                        transform: "translateX(4px)",
                        "& .MuiListItemText-primary": {
                          color: theme.palette.primary.main,
                          fontWeight: "bold",
                        },
                      },
                      "&.Mui-selected": {
                        backgroundColor: theme.palette.action.selected,
                        "& .MuiListItemText-primary": {
                          color: theme.palette.primary.main,
                          fontWeight: "bold",
                        },
                      },
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 0,
                        mr: 2,
                        justifyContent: "center",
                        color: theme.palette.primary.main,
                      }}
                    >
                      {React.cloneElement(subItem.icon, {
                        style: { fontSize: 16 },
                      })}
                    </ListItemIcon>
                    <ListItemText
                      primary={subItem.name}
                      sx={{
                        "& .MuiListItemText-primary": {
                          fontSize: "0.8rem",
                        },
                      }}
                    />
                  </ListItemButton>
                ))}
              </List>
            </Collapse>
          )}
        </>
      ) : (
        <ListItemButton
          component={Link}
          to={item.path}
          selected={location.pathname === item.path}
          sx={{
            minHeight: 48,
            px: collapsed ? 1.5 : 2.5,
            margin: "4px 16px",
            borderRadius: "8px",
            transition: "all 0.2s ease-in-out",
            "&:hover": {
              backgroundColor: theme.palette.action.hover,
              transform: "translateX(4px)",
              "& .MuiListItemText-primary": {
                color: theme.palette.primary.main,
                fontWeight: "bold",
              },
              "& .MuiListItemIcon-root": {
                color: theme.palette.primary.main,
              },
            },
            "&.Mui-selected": {
              backgroundColor: theme.palette.action.selected,
              "& .MuiListItemText-primary": {
                color: theme.palette.primary.main,
                fontWeight: "bold",
              },
              "& .MuiListItemIcon-root": {
                color: theme.palette.primary.main,
              },
            },
          }}
        >
          <ListItemIcon
            sx={{
              minWidth: 0,
              mr: collapsed ? 0 : 3,
              justifyContent: "center",
              color: theme.palette.primary.main,
            }}
          >
            <Tooltip
              title={item.title}
              placement="right"
              disableHoverListener={!collapsed}
            >
              {item.icon}
            </Tooltip>
          </ListItemIcon>
          {!collapsed && (
            <ListItemText
              primary={item.title}
              sx={{
                "& .MuiListItemText-primary": {
                  fontSize: "0.875rem",
                  fontWeight: 500,
                },
              }}
            />
          )}
        </ListItemButton>
      )}
    </Box>
  );

  const drawer = (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* Header */}
      <Box
        sx={{
          p: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: collapsed ? "center" : "space-between",
          borderBottom: "1px solid",
          borderColor: "divider",
          minHeight: 64,
          flexShrink: 0,
        }}
      >
        {!collapsed && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
            }}
          >
            <Typography
              variant="h6"
              component="span"
              sx={{
                color: theme.palette.text.primary,
                fontSize: "1.5rem",
                fontWeight: "bold",
              }}
            >
              CS
            </Typography>
            <Typography
              variant="h6"
              component="span"
              sx={{
                color: theme.palette.primary.main,
                fontSize: "1.5rem",
                fontWeight: "bold",
                ml: 1,
              }}
            >
              Portal
            </Typography>
          </Box>
        )}
        <IconButton onClick={handleDrawerToggle} size="small">
          <MenuIcon />
        </IconButton>
      </Box>

      <Box
        sx={{
          flexGrow: 1,
          overflow: "auto",
        }}
      >
        <List>{menuItems.map(renderMenuItem)}</List>
      </Box>

      <Box
        sx={{
          borderTop: "1px solid",
          borderColor: "divider",
          display: "flex",
          justifyContent: "center",
          alignItems: "flex-start",
          minHeight: {
            xs: 120,
            sm: 140,
            md: collapsed ? 100 : 160,
            lg: 180,
          },
          flexShrink: 0,
          backgroundColor: theme.palette.background.default,
        }}
      >
        <Box
          component="img"
          src="/icon.jpg"
          alt="Company Logo"
          sx={{
            height: {
              xs: collapsed ? 40 : 60,
              sm: collapsed ? 50 : 70,
              md: collapsed ? 60 : 90,
              lg: collapsed ? 70 : 120,
            },
            width: "auto",
            objectFit: "contain",
            maxWidth: {
              xs: collapsed ? "33px" : "148px",
              sm: collapsed ? "38px" : "178px",
              md: collapsed ? "43px" : "198px",
              lg: collapsed ? "48px" : "218px",
            },
            display: "block",
            mt: { xs: 0.5, sm: 1 },
          }}
        />
      </Box>
    </Box>
  );

  return (
    <motion.div
      animate={{ width: drawerWidth }}
      transition={{ duration: 0.3, ease: "easeInOut" }}
      style={{
        width: drawerWidth,
        flexShrink: 0,
        height: `calc(100vh - ${navbarHeight}px)`,
        position: "relative",
      }}
    >
      <Box
        sx={{
          width: drawerWidth,
          height: `calc(100vh - ${navbarHeight}px)`,
          borderRight: "1px solid",
          borderColor: theme.palette.divider,
          backgroundColor: theme.palette.background.paper,
          overflow: "hidden",
          position: "fixed",
          top: `${navbarHeight}px`,
          left: 0,
          bottom: 0,
          zIndex: 1100,
          display: "flex",
          flexDirection: "column",
          "&::after": {
            content: '""',
            position: "absolute",
            top: 0,
            right: 0,
            bottom: 0,
            width: !collapsed ? "3px" : "0px",
            backgroundColor: theme.palette.primary.main,
            transition: "width 0.3s ease-in-out",
            zIndex: 1,
          },
        }}
      >
        {drawer}
      </Box>
    </motion.div>
  );
};

export default CsSidebar;
